type KeyboardMotionEvent = Event & {
  keyboardHeight: number;
  screenHeight: number;
  animationDuration: number;
  animationCurve?: number;
};

/** Decode the optional fields supplied only by our pinned native plugin. */
function isKeyboardMotionEvent(event: Event): event is KeyboardMotionEvent {
  const data = event as Partial<KeyboardMotionEvent>;
  return (
    typeof data.keyboardHeight === "number" &&
    Number.isFinite(data.keyboardHeight) &&
    data.keyboardHeight >= 0 &&
    typeof data.screenHeight === "number" &&
    Number.isFinite(data.screenHeight) &&
    data.screenHeight > data.keyboardHeight &&
    typeof data.animationDuration === "number" &&
    Number.isFinite(data.animationDuration) &&
    data.animationDuration >= 0
  );
}

/**
 * Start body resizing with iOS's keyboard, instead of waiting for Capacitor's
 * delayed settle resize (keyboard duration + 200ms in keyboard 8.0.5).
 * The pinned native patch adds timing to the existing window events. Older
 * binaries omit it and retain the plugin's normal resize behavior.
 */
export function installNativeKeyboardMotion() {
  let animation: Animation | undefined;
  const resize = (event: Event) => {
    if (!isKeyboardMotionEvent(event)) return;
    const { keyboardHeight, screenHeight, animationDuration, animationCurve } =
      event;

    const body = document.body;
    // Read the presentation height before cancelling an interrupted animation.
    const from = body.getBoundingClientRect().height;
    animation?.cancel();
    animation = undefined;
    const to = screenHeight - keyboardHeight;
    body.style.height = keyboardHeight > 0 ? `${to}px` : "";
    if (
      from === to ||
      animationDuration === 0 ||
      !body.animate ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    )
      return;

    // UIKit's standard curves map to CSS; its keyboard-specific curve (7) is
    // approximated by ease-out. This is a WebView animation, not a UIKit spring.
    const easing =
      animationCurve === 0
        ? "ease-in-out"
        : animationCurve === 1
          ? "ease-in"
          : animationCurve === 3
            ? "linear"
            : "ease-out";
    animation = body.animate([{ height: `${from}px` }, { height: `${to}px` }], {
      duration: animationDuration,
      easing,
    });
    // Inline height already holds the destination. No finish callback/timer can
    // overwrite a newer keyboard transition or keep the page short on dismissal.
  };
  window.addEventListener("keyboardWillShow", resize);
  window.addEventListener("keyboardWillHide", resize);
  return () => {
    animation?.cancel();
    window.removeEventListener("keyboardWillShow", resize);
    window.removeEventListener("keyboardWillHide", resize);
  };
}
